import React, { PureComponent } from "react";
import { connect } from "react-redux";
import flow from "lodash/flow";
import styled from "styled-components/macro";

import { dragSourceCollect, dragTargetCollect, draggable } from "../../helpers/DragAndDrop";
import { DragSource, DropTarget } from "react-dnd";
import {
  SideListRowStyled,
  SideListExpander,
  SideListContentItem,
  SideListContentEdit,
  SideListContentWrapper,
  SideListContentText,
  SideListRowOuter,
} from "../common/StyledComponents";
import { getSideBgColour } from "../../helpers/StyleHelpers";
import WrappedTooltip from "../common/WrappedTooltip";
import { formatWithLocale as format, parseDate } from "../../helpers/DateHelpers";

import unlinkTaskIcon from "../../../icons/IconUnlink.png";
import unlinkTaskIconHover from "../../../icons/IconUnlinkHover.png";
import unlinkTaskIconPressed from "../../../icons/IconUnlinkPressed.png";
import { IconSettingsButton22x22 } from "../common/Buttons";
import { iRootState, Dispatch } from "src/rematch/store";
import { Action } from "@rematch/core";
import { IDragNDropPayload, IRemoveViewItemPayload, IMoveOrResizeManyPayload } from "src/rematch/interfaces/payloads/itemReducers";
import { IHierarchyItem } from "src/backend/interfaces/IItem";

const imgs = [unlinkTaskIcon, unlinkTaskIconHover, unlinkTaskIconPressed];

const dragSource = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  beginDrag(props: any) {
    return { task: props.task };
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  canDrag(props: any) {
    if (props.checkedOutToSomeoneElse) {
      return false;
    }
    return true;
  },
};

const dropTarget = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  drop(props: any, monitor: any) {
    const item = monitor.getItem();
    props.onApplyTaskOnProperty({ sourceItemId: item.task.id, targetItemId: props.task.id });
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  canDrop(props: any, monitor: any) {
    const item = monitor.getItem();
    if (props.checkedOutToSomeoneElse) {
      return false;
    }
    return props.canDropAccordingToConfiguration(item.task.objectType, props.task.objectType);
  },
};

const RemoveResourceButton = styled(IconSettingsButton22x22)`
  height: 100%;
  flex: 0 0 14px;
  width: 14px;
  display: flex;
  background-size: contain;
  margin-left: 8px;
  margin-right: 8px;
`;

interface ISideListRowProps {
  hasChildren: boolean;
  isExpanded: boolean;
  dateFormat: string;
  timeFormat: string;
  showTimeInTable: boolean;
  isSelected: boolean;
  isEdited: boolean;
  hasEditedItem: boolean;
  isResourcedTask: boolean;
  task: IHierarchyItem;
  viewIndex: number;
  resourceId: number;
  property: string | number;
  visibleTaskColumns: { name: string, width: number }[];
  dragUnderway: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  connectDragSource: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  connectDropTarget: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isOver: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  canDrop: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isDragged: any;
  taskHeight: number;
  checkedOutToSomeoneElse: boolean;
  onToggleExpand: (taskId: number, property: string | number) => void;
  canDropAccordingToConfiguration: (draggedItemObjectType: number, dropTargetObjectType: number) => boolean;
  gridColumn: (objectType: number, columnNum: number) => string | number;
  onSelectItem: (taskId: number) => Action<number,void>;
  onEditItem: (taskId: number) => Action<number,void>;
  onApplyTaskOnProperty: (payload: IDragNDropPayload) => Action<IDragNDropPayload,boolean>;
  onRemoveItem: (payload: IRemoveViewItemPayload) => Action<IRemoveViewItemPayload,boolean>;
  moveOrResizeManyAsync: (payload: IMoveOrResizeManyPayload) => Action<IMoveOrResizeManyPayload, void>;
}

interface ISideListRowState {
  start: string;
  end: string;
  startHasError: boolean;
  endHasError: boolean;
}

class SideListRow extends PureComponent<ISideListRowProps,ISideListRowState> {
  constructor(props: ISideListRowProps) {
    super(props);
    this.state = {
      start: this.getColumnTextValue("start"),
      end: this.getColumnTextValue("end"),
      startHasError: false,
      endHasError: false,
    };
  }
  componentDidMount = () => {
    for (const imgURL of imgs) {
      const img = new Image();
      img.src = imgURL;
    }
  };

  getExpandSymbol = () => {
    if (!this.props.hasChildren) {
      return "";
    }
    if (this.props.isExpanded) {
      return "-";
    }
    return "+";
  };

  getDateFormat() {
    return this.props.dateFormat + (this.props.showTimeInTable ? " " + this.props.timeFormat : "");
  }

  getColumnTextValue(columnName): string {
    const field = this.props.task[columnName];
    if (field instanceof Date) {
      let property = columnName;
      let dateFormat = this.props.dateFormat;
      //Check if text-field is based on start or end, which may have time-part
      if(property.startsWith("text")) {
        const fieldNum = parseInt(property.substring(4));
        property = this.props.gridColumn(this.props.task.objectType, fieldNum);
      }
      if(property === "start" || property === "end") {
        dateFormat = this.getDateFormat();
      }
      return format(field, dateFormat);
    }
    return field ? field.toString() : "-";
  }

  editStart = (e) => {
    this.validateDates(e.target.value, this.state.end);
  };

  editEnd = (e) => {
    this.validateDates(this.state.start, e.target.value);
  }

  validateDates = (startText: string, endText: string) => {
    const startDate = parseDate(startText, this.getDateFormat());
    const endDate = parseDate(endText, this.getDateFormat());
    let startIsInvalid = isNaN(Number(startDate));
    let endIsInvalid = isNaN(Number(endDate));
    if(!startIsInvalid && !endIsInvalid && startDate > endDate) {
      startIsInvalid = true;
      endIsInvalid = true;
    }
    this.setState((_prevState) => {
      return {
        start: startText,
        end: endText,
        startHasError: startIsInvalid,
        endHasError: endIsInvalid,
      };
    });
  }

  resetState = () => {
    this.validateDates(this.getColumnTextValue("start"),this.getColumnTextValue("end"));
  }

  saveChanges = () => {
    const startDate = parseDate(this.state.start, this.getDateFormat());
    const endDate = parseDate(this.state.end, this.getDateFormat());
    const task = this.props.task;

    if( !this.state.startHasError && !this.state.endHasError && 
      ( startDate?.getTime() !== task.start?.getTime() || endDate?.getTime() !== task.end?.getTime() ) ) {
      this.props.moveOrResizeManyAsync({
        [task.id]: {
          itemId: task.id,
          newStart: startDate,
          newEnd: endDate,
        }
      });
    }
  }

  renderColumnFields() {
    let i = 0;
    const fields = [];
    if (this.props.isResourcedTask) {
      fields.push(
        <RemoveResourceButton
          key={i++}
          onClick={() => {
            this.props.onRemoveItem({ viewIndex: this.props.viewIndex, resourceItemId: this.props.resourceId, taskItemId: this.props.task.id });
          }}
          image={unlinkTaskIcon}
          imageHover={unlinkTaskIconHover}
          imagePressed={unlinkTaskIconPressed}
        />
      );
    } else {
      fields.push(
        <SideListExpander
          key={i++}
          onClick={(e) => {
            e.stopPropagation();
            this.props.onToggleExpand(this.props.task.id, this.props.property);
          }}
        >
          {this.getExpandSymbol()}
        </SideListExpander>
      );
    }
    let paddingLeft = this.props.task.hierarchyLevel * 10;
    for (const { name, width } of this.props.visibleTaskColumns) {
      let property: string | number = name;
      if(name.startsWith("text")) {
        const fieldNum = parseInt(name.substring(4));
        property = this.props.gridColumn(this.props.task.objectType, fieldNum);
      }
      const fieldIsEditable = ( property === "start" || property === "end" ) && this.props.isEdited;
      fields.push(
        <SideListContentWrapper key={i++} width={width}>
          <WrappedTooltip key={i++} task={this.props.task} dragUnderway={this.props.dragUnderway || this.props.hasEditedItem}>
            <SideListContentItem key={i++} paddingLeft={paddingLeft + (fieldIsEditable ? 0 : 5)}>
              {!fieldIsEditable && <SideListContentText>{this.getColumnTextValue(name)}</SideListContentText>}
              {fieldIsEditable && property === "start" && <SideListContentEdit onChange={this.editStart} hasError={this.state.startHasError} value={this.state.start === "-" ? "" : this.state.start}/>}
              {fieldIsEditable && property === "end" && <SideListContentEdit onChange={this.editEnd} hasError={this.state.endHasError} value={this.state.end === "-" ? "" : this.state.end}/>}
            </SideListContentItem>
          </WrappedTooltip>
        </SideListContentWrapper>
      );
      paddingLeft = 0; //Only use padding for the first column.
    }
    return fields;
  }

  selectItem = (e) => {
    if (!this.props.dragUnderway) {
      if (this.props.isSelected) {
        // If already selected, deselect
        this.props.onSelectItem(null);
      } else {
        this.props.onSelectItem(this.props.task.id);
      }
      e.stopPropagation();
    }
  };

  editItem = (e) => {
    if(this.props.isEdited) {
      this.props.onEditItem(null);
      this.saveChanges();
    } else if(!this.props.hasEditedItem) {
      this.resetState();
      this.props.onEditItem(this.props.task.id);
    }
    e.stopPropagation();
  };

  render() {
    //These props come from the drag and drop injection.
    const { connectDragSource, connectDropTarget, isOver, canDrop, isDragged } = this.props;

    const bgColour = getSideBgColour(this.props.isSelected, this.props.dragUnderway, isDragged, isOver, canDrop);

    return connectDropTarget(
      connectDragSource(
        <div>
          <SideListRowOuter height={this.props.taskHeight}>
            <SideListRowStyled
              preventOutline={this.props.dragUnderway && !isDragged}
              isSelected={this.props.isSelected}
              bgColour={bgColour}
              checkedOutToSomeoneElse={this.props.checkedOutToSomeoneElse}
              onClick={this.selectItem}
              onDoubleClick={this.editItem}
            >
              {this.renderColumnFields()}
            </SideListRowStyled>
          </SideListRowOuter>
        </div>
      )
    );
  }
}

const mapState = (state: iRootState, ownProps: ISideListRowProps) => ({
  dateFormat: state.UI.displaySettings.dateFormat,
  timeFormat: state.UI.displaySettings.timeFormat,
  showTimeInTable: state.UI.displaySettings.showTimeInTable,
  isSelected: state.items.selectedItem === ownProps.task.id,
  isEdited: state.items.editedItem === ownProps.task.id,
  hasEditedItem: state.items.editedItem !== null,
  taskHeight: state.UI.displaySettings.taskHeight,
  canDropAccordingToConfiguration: (draggedItemObjectType: number, dropTargetObjectType: number) =>
    state.common.configuration.dragndropConfigurations.some(
      (dndConfig) => dndConfig.sourceObjectType === draggedItemObjectType && dndConfig.targetObjectType === dropTargetObjectType
    ),
  gridColumn: (objectType: number, columnNum: number) => { 
    const userColumns = state.UI.displaySettings.gridColumns[objectType];
    if(userColumns) {
      return userColumns[columnNum];
    }
    return state.common.configuration.objectTypeConfigurations.get(objectType).gridColumns[columnNum];
  },
});

const mapDispatch = (dispatch: Dispatch) => ({
  onSelectItem: dispatch.items.selectItem,
  onEditItem: dispatch.items.editItem,
  onApplyTaskOnProperty: dispatch.items.dragndropAsync,
  onRemoveItem: dispatch.items.removeViewItem,
  moveOrResizeManyAsync: dispatch.items.moveOrResizeManyAsync,
});

export const DnDSideListRow = flow(
  DragSource(draggable, dragSource, dragSourceCollect),
  DropTarget(draggable, dropTarget, dragTargetCollect),
  connect(mapState, mapDispatch)
)(SideListRow);
